import { useEffect, useState } from 'react';
import { api } from '../api/client';

const EVENT_TYPES = ['Discipline', 'Présence', 'Sanction', 'Conseil de discipline', 'Comportement', 'Félicitation', 'Autre'];
const PRESENCE_STATUS = ['Présent', 'Absent', 'Retard', 'Exclu'];
const EMPTY = {
  studentId: '', eventDate: new Date().toISOString().slice(0, 10),
  eventType: 'Discipline', description: '', presenceStatus: 'Présent',
  sanction: '', disciplineCouncilDate: '', behaviorScore: '',
  decision: '', responsible: '',
};
const TYPE_COLOR = {
  'Discipline': 'red', 'Sanction': 'red', 'Conseil de discipline': 'red',
  'Absence': 'orange', 'Retard': 'gold', 'Comportement': 'navy',
  'Félicitation': 'green', 'Présence': 'green', 'Autre': 'gray',
};

export default function VieScolaire() {
  const [rows, setRows] = useState([]);
  const [students, setStudents] = useState([]);
  const [error, setError] = useState('');
  const [modal, setModal] = useState(null);
  const [view, setView] = useState(null);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  function load() {
    api.get('/registry/school-life').then((d) => setRows((Array.isArray(d) ? d : (d?.records || [])).map((r) => ({ id: r.id, ...r.data })))).catch(() => {});
  }

  useEffect(() => {
    load();
    api.get('/students').then(setStudents).catch(() => {});
  }, []);

  const filtered = rows.filter((r) => {
    const q = search.toLowerCase();
    const matchQ = !q || (r.studentName || '').toLowerCase().includes(q);
    const matchT = !typeFilter || r.eventType === typeFilter;
    return matchQ && matchT;
  });

  return (
    <>
      <div className="kpi-grid">
        <div className="kpi"><span>Total événements</span><strong>{rows.length}</strong></div>
        <div className="kpi red"><span>Discipline</span><strong>{rows.filter((r) => ['Discipline', 'Sanction', 'Conseil de discipline'].includes(r.eventType)).length}</strong></div>
        <div className="kpi green"><span>Félicitations</span><strong>{rows.filter((r) => r.eventType === 'Félicitation').length}</strong></div>
        <div className="kpi gold"><span>Retards / Absences</span><strong>{rows.filter((r) => ['Retard', 'Absence'].includes(r.eventType)).length}</strong></div>
      </div>

      <div className="panel">
        <div className="panel-head">
          <h2>Vie scolaire</h2>
          <div style={{ display: 'flex', gap: 8 }}>
            <input placeholder="Nom élève…" value={search} onChange={(e) => setSearch(e.target.value)} style={{ width: 180 }} />
            <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} style={{ width: 160 }}>
              <option value="">Tous les types</option>
              {EVENT_TYPES.map((t) => <option key={t}>{t}</option>)}
            </select>
            <button className="btn btn-sm" onClick={() => setModal({ ...EMPTY })}>+ Nouveau fait</button>
          </div>
        </div>
        <div className="panel-body">
          {error && <div className="form-error">{error}</div>}
          {filtered.length === 0 ? <div className="empty">Aucun fait de vie scolaire.</div> : (
            <div className="table-wrap"><table>
              <thead>
                <tr>
                  <th>Élève</th><th>Date</th><th>Type</th><th>Présence</th>
                  <th>Description</th><th>Sanction</th><th>Note conduite</th><th>Responsable</th><th></th>
                </tr>
              </thead>
              <tbody>{filtered.map((r) => (
                <tr key={r.id}>
                  <td><b>{r.studentName}</b><br /><small>{r.className}</small></td>
                  <td className="mono">{r.eventDate ? new Date(r.eventDate).toLocaleDateString('fr-FR') : '—'}</td>
                  <td><span className={`badge ${TYPE_COLOR[r.eventType] || 'gray'}`}>{r.eventType}</span></td>
                  <td>{r.presenceStatus || '—'}</td>
                  <td style={{ maxWidth: 200, whiteSpace: 'normal' }}>{r.description || '—'}</td>
                  <td>{r.sanction || '—'}</td>
                  <td className="num">{r.behaviorScore || '—'}</td>
                  <td>{r.responsible || '—'}</td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button className="btn-ghost btn-sm" onClick={() => setView(r)}>Fiche</button>
                    <button className="btn-ghost btn-sm" onClick={() => setModal(r)}>Modifier</button>
                  </td>
                </tr>
              ))}</tbody>
            </table></div>
          )}
        </div>
      </div>

      {modal && (
        <VieScolaireModal
          initial={modal}
          students={students}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}
    </>
  );
}

function VieScolaireModal({ initial, students, onClose, onSaved }) {
  const isEdit = !!initial.id;
  const [f, setF] = useState({ ...EMPTY, ...initial });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  async function save() {
    if (!f.eventType || !f.eventDate) { setError('Type et date sont obligatoires.'); return; }
    setSaving(true);
    try {
      const student = students.find((s) => s.id === f.studentId);
      const payload = { ...f, studentName: student ? `${student.lastName} ${student.firstName}` : f.studentName };
      if (isEdit) await api.patch(`/registry/school-life/${initial.id}`, { data: payload });
      else await api.post('/registry/school-life', { data: payload, label: payload.studentName || payload.designation || payload.reportedDisease || payload.formType || payload.eventType || '', searchText: JSON.stringify(payload).slice(0, 200) });
      onSaved();
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  }

  return (
    <div className="modal-overlay">
      <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
        <h2>{isEdit ? 'Modifier le fait' : 'Nouveau fait de vie scolaire'}</h2>
        {error && <div className="form-error">{error}</div>}
        <div className="fieldset-title">Identification</div>
        <div className="grid-3">
          <div className="field"><label>Élève</label>
            <select value={f.studentId} onChange={set('studentId')}>
              <option value="">Sélectionner…</option>
              {students.map((s) => <option key={s.id} value={s.id}>{s.lastName} {s.firstName}</option>)}
            </select>
          </div>
          <div className="field"><label>Date *</label><input type="date" value={f.eventDate} onChange={set('eventDate')} /></div>
          <div className="field"><label>Type *</label>
            <select value={f.eventType} onChange={set('eventType')}>
              {EVENT_TYPES.map((t) => <option key={t}>{t}</option>)}
            </select>
          </div>
        </div>
        <div className="fieldset-title">Détails</div>
        <div className="grid-3">
          <div className="field"><label>Présence</label>
            <select value={f.presenceStatus} onChange={set('presenceStatus')}>
              {PRESENCE_STATUS.map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div className="field"><label>Note de comportement /20</label>
            <input type="number" min="0" max="20" step="0.5" value={f.behaviorScore} onChange={set('behaviorScore')} />
          </div>
          <div className="field"><label>Responsable suivi</label><input value={f.responsible} onChange={set('responsible')} /></div>
        </div>
        <div className="field"><label>Description</label><textarea value={f.description} onChange={set('description')} rows={2} /></div>
        <div className="grid-3">
          <div className="field"><label>Sanction</label><input value={f.sanction} onChange={set('sanction')} /></div>
          <div className="field"><label>Date conseil de discipline</label><input type="date" value={f.disciplineCouncilDate} onChange={set('disciplineCouncilDate')} /></div>
        </div>
        <div className="field"><label>Décision / Suivi</label><textarea value={f.decision} onChange={set('decision')} rows={2} /></div>
        <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
          <button className="btn" onClick={save} disabled={saving} style={{ width: 'auto', padding: '0 22px' }}>{saving ? 'Enregistrement…' : 'Enregistrer'}</button>
          <button className="btn-ghost" onClick={onClose}>Annuler</button>
        </div>
      </div>
    </div>
  );
}

function ViewModal({ item, title, fields, onClose, onEdit }) {
  if (!item) return null;
  return (
    <div className="modal-overlay">
      <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
        <h2>Fiche — {title}</h2>
        <div className="view-grid">
          {fields.map(([label, key]) => item[key] != null && item[key] !== '' ? (
            <div key={key} className="view-row">
              <span className="view-label">{label}</span>
              <span className="view-value">{String(item[key])}</span>
            </div>
          ) : null)}
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
          <button className="btn" onClick={onEdit} style={{ width: 'auto', padding: '0 22px' }}>Modifier</button>
          <button className="btn-ghost" onClick={onClose}>Fermer</button>
        </div>
      </div>
    </div>
  );
}
